/**
 * Шаблоны агроцикла — из `GET /agrocycle/templates` и деталей их версий.
 *
 * Стадии в каноне НЕТ (решение 3 плана `astra-on-canon`): шаблон знает слоты с
 * порядком и каталожной работой, а стадия — производная группировка, правило
 * которой объявляет владелец в `docs/model/season-stages.yaml`. Сервер уже
 * применяет это правило к РАБОТАМ КАМПАНИЙ и отдаёт стадию строкой сводки,
 * поэтому раскладка слотов по стадиям берётся оттуда: слот, по которому в
 * срезе запущена кампания, наследует её стадию.
 *
 * Слот, кампанию по которому ещё не запускали, стадии не получает — и это НЕ
 * «прочее». Сложить такие слоты в общую корзину значило бы выдать умолчание
 * клиента за решение владельца, ровно то, что запрещено загрузчику справочника
 * на сервере. Поэтому они собраны в отдельную группу, названную вслух, а
 * причина написана в оговорках шаблона.
 *
 * Черновики сюда не попадают: незаконченная работа владельца — не альтернатива
 * опубликованной версии, и показывать её рядом как равную нельзя.
 */

import { api, type QueryParams, type Schemas } from '../../../api/client';
import type { CycleTemplate, TemplateStage, TemplateWork, TemplatesFile } from '../../data/types';
import { type DatasetAdapter, once, paramsKey } from './adapter';
import { KIND_OF } from './works';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  unplacedName: {
    id: 'data.templates.unplacedStage',
    defaultMessage: 'Этап не назван',
    description: 'Название группы работ шаблона, для которых справочник не назвал этап (группа в плане агроцикла)',
  },
  unplacedNote: {
    id: 'data.templates.unplacedNote',
    defaultMessage: 'Этап — производная группировка, а не поле шаблона: его называет справочник владельца по виду работы, и приходит он вместе с запущенной кампанией. Работы, по которым в этом срезе кампаний ещё нет, стоят отдельной группой — это отсутствие ответа, а не этап «прочее».',
    description: 'Оговорка под планом шаблона: почему часть работ собрана в группу «Этап не назван»',
  },
  notRequired: {
    id: 'data.templates.notRequired',
    defaultMessage: 'шаблон не требует эту работу',
    description: 'Условие выполнения работы шаблона, когда причина не названа (строчная фраза в колонке «Условие»)',
  },
  noSource: {
    id: 'data.templates.noSource',
    defaultMessage: 'источник не назван',
    description: 'Источник нормы или окна работы шаблона не назван (строчная фраза)',
  },
});


type TemplateRow = Schemas['TemplateRow'];
type SlotRow = Schemas['SlotOut'];
type StageRef = Schemas['SeasonStageRefOut'];

export const TEMPLATES_SET = 'templates.json';

/** Группа для слотов, стадию которых справочник в этом срезе не назвал. */
export const UNPLACED_STAGE_ID = 'stage-unplaced';
export function unplacedStageName(): string {
  return tr(m.unplacedName);
}


/** Слот шаблона → работа шаблона в форме прототипа. */
export function toTemplateWork(slot: SlotRow): TemplateWork {
  return {
    id: String(slot.work_def_id ?? slot.catalog_code),
    name: slot.name,
    kind: KIND_OF[slot.category],
    window: { from: slot.window_from ?? '', to: slot.window_to ?? '' },
    // Нормы ресурса приходят словарём как есть: их состав — дело каталога, и
    // переименовывать ключи здесь значило бы завести второй словарь норм.
    per_ha: slot.resource_norms as Record<string, string | number | null>,
    // Слот канона — ОДИН запуск работы. Повторы шаблона — это отдельные слоты
    // со своими окнами, поэтому диапазона «от скольких до скольких раз» у
    // канонического слота нет и быть не может.
    count_range: [1, 1],
    // «Условная» прототипа = слот, который шаблон не требует. Отменённый слот
    // называет свою причину — она и печатается вместо общих слов.
    conditional: slot.is_required
      ? null
      : slot.cancelled_reason || tr(m.notRequired),
    source: slot.evidence_source || slot.evidence || tr(m.noSource),
  };
}

/** Окно группы — от самого раннего начала до самого позднего конца. */
function stageWindow(works: readonly TemplateWork[]): { from: string; to: string } {
  const from = works.map((w) => w.window.from).filter(Boolean).sort();
  const to = works.map((w) => w.window.to).filter(Boolean).sort();
  return { from: from[0] ?? '', to: to[to.length - 1] ?? '' };
}

/**
 * Слоты по стадиям в порядке справочника.
 *
 * Порядок стадий берётся из `order` справочника, а не из порядка слотов: две
 * стадии, чередующиеся в шаблоне, иначе разъехались бы по разным местам
 * списка.
 */
export function toStages(
  slots: readonly SlotRow[],
  stageOf: ReadonlyMap<number, StageRef>,
): TemplateStage[] {
  const groups = new Map<string, { ref: StageRef | null; works: TemplateWork[] }>();

  for (const slot of slots) {
    const ref = slot.work_def_id === null ? null : (stageOf.get(slot.work_def_id) ?? null);
    const key = ref ? ref.id : UNPLACED_STAGE_ID;
    let group = groups.get(key);
    if (!group) {
      group = { ref, works: [] };
      groups.set(key, group);
    }
    group.works.push(toTemplateWork(slot));
  }

  const rows = [...groups.entries()].map(([id, group]) => ({
    id,
    name: group.ref?.name ?? unplacedStageName(),
    // Группа без стадии идёт последней: её место в сезоне неизвестно, и
    // вставлять её между названными стадиями значило бы это место придумать.
    order: group.ref?.order ?? Number.MAX_SAFE_INTEGER,
    works: group.works,
  }));
  rows.sort((a, b) => a.order - b.order);

  return rows.map(({ id, name, works }) => ({
    id,
    name,
    window: stageWindow(works),
    works,
  }));
}

/**
 * Карта «слот → стадия» на весь срез: один обход сводки вместо обхода на цикл.
 *
 * Сводка без оси цикла отвечает про ВСЕ кампании среза, и каждая её работа
 * несёт и `work_def_id`, и стадию. Спрашивать сводку по каждому циклу отдельно
 * значило бы задать один и тот же вопрос столько раз, сколько в районе культур.
 */
async function stageIndex(params: QueryParams): Promise<Map<number, StageRef>> {
  const query: QueryParams = { ...params, summary: true, weeks: 0 };
  const picture = await once(`picture|${paramsKey(query)}`, () => api.seasonPicture(query));
  const map = new Map<number, StageRef>();
  for (const row of picture.summary?.works ?? []) map.set(row.work_def_id, row.stage);
  return map;
}

function toTemplate(
  row: TemplateRow,
  slots: readonly SlotRow[],
  stageOf: ReadonlyMap<number, StageRef>,
): CycleTemplate {
  const stages = toStages(slots, stageOf);
  const notes: string[] = [];
  if (stages.some((stage) => stage.id === UNPLACED_STAGE_ID)) notes.push(tr(m.unplacedNote));
  return {
    id: String(row.id),
    crop: row.crop_code ?? '',
    season_label: row.season_label,
    version: row.version,
    stages,
    notes,
  };
}

export const templatesAdapter: DatasetAdapter = {
  domain: 'templates',

  match(name) {
    return name === TEMPLATES_SET;
  },

  async load(_name, params) {
    return once(`templates|${paramsKey(params)}`, async (): Promise<TemplatesFile> => {
      const [list, stageOf] = await Promise.all([
        api.agrocycleTemplates(params),
        stageIndex(params),
      ]);
      const published = list.items.filter((row) => row.lifecycle === 'published');
      const details = await Promise.all(
        published.map((row) => api.agrocycleTemplate(row.id, params)),
      );
      return {
        templates: published.map((row, index) =>
          toTemplate(row, details[index]!.slots, stageOf),
        ),
      };
    });
  },
};
